import * as ProjectSelectors from "@app/project/store/selectors/projects.selectors";
import * as DeviceMetadataSelectors from "./device-metadata.selectors";
import * as DeviceSelectors from "./devices.selectors";

import { CHDevice, SecurityZoneInfo } from "@app/project/model/ch-device";
import { CHServiceState, initialServiceState } from "@app/project/store/clarehome.state";
import { LoadingState, initialCHObjectState } from "@app/root-store/api-result.state";
import { offlineClareStatus, onlineClareStatus, unconfiguredClareStatus } from "@app/shared/model/clare-status.model";
import { createFeatureSelector, createSelector } from "@ngrx/store";

import { CHService } from "@app/project/model/ch-service";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { CHServiceSelectors } from "..";

const selectState = createFeatureSelector<CHServiceState>("chservice");

const selectLiteServiceState = createFeatureSelector<CHServiceState>("chliteservice");

export const selectChService = (instanceId: string) =>
    createSelector(selectState, (state: CHServiceState) => state?.result?.[instanceId] || null);

export const selectChServicesState = createSelector(selectState, ProjectSelectors.getUuid, (state: CHServiceState, uuid: string) =>
    state?.uuid ? state : initialServiceState(uuid)
);

export const getChServicesByType = (serviceDefId: string) =>
    createSelector(selectChServicesState, (state: CHServiceState) => {
        let servicesArray;
        if (state.result) {
            servicesArray = Object.values(Object.values(state.result));
        }
        const servicesOfType: CHService[] = [];

        if (servicesArray) {
            servicesArray.forEach((service: CHService) => {
                if (service?.definitionId === serviceDefId) {
                    servicesOfType.push(service);
                }
            });
        }

        return servicesOfType;
    });

export const getChServiceListForCurrentProject = createSelector(selectChServicesState, (state: CHServiceState): CHService[] => {
    const { result } = state;
    if (!!result) {
        const servicesList = Object.values(result);

        servicesList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        return servicesList;
    }
    return [];
});

export const isLoadingCurrentProjectServiceAndDeviceInfo = createSelector(
    selectState,
    DeviceSelectors.selectChDevicesState,
    DeviceMetadataSelectors.selectChDeviceMetadataState,
    selectLiteServiceState,
    (state: CHServiceState, deviceState, metadataState, liteServicesState) => {
        if (state && deviceState && metadataState && liteServicesState) {
            return (
                state.callState !== LoadingState.loaded ||
                deviceState.callState !== LoadingState.loaded ||
                metadataState.callState !== LoadingState.loaded ||
                liteServicesState.callState !== LoadingState.loaded
            );
        }
        return true;
    }
);

// Lite service info state;
export const selectChLiteServices = createSelector(selectLiteServiceState, ProjectSelectors.getUuid, (state, uuid) =>
    state?.uuid ? state : initialCHObjectState(null, uuid)
);

const getServiceStatus = service => {
    if (!service.isConfigured) {
        return unconfiguredClareStatus;
    }
    if (!service.isValid) {
        return offlineClareStatus;
    }
    return onlineClareStatus;
};

export const getServiceStatusCounts = createSelector(selectChLiteServices, services => {
    const statusitems = { Online: 0, Offline: 0, Unconfigured: 0 };
    const AUDIO_SOURCE_DEF_ID = "com.clarecontrols.widgets.audio.source";
    const INTRUSION_PERIPHERAL_DEF_ID = "com.clarecontrols.widgets.intrusion.peripheral";
    const SERVICE_DEF_BLACKLIST = [AUDIO_SOURCE_DEF_ID, INTRUSION_PERIPHERAL_DEF_ID];
    for (const uid in services.result) {
        if (
            (services.result[uid] && !SERVICE_DEF_BLACKLIST.includes(services.result[uid].definitionId)) ||
            services.result[uid].instanceId.includes("ZWave")
        ) {
            const status = getServiceStatus(services.result[uid]);
            statusitems[status[0]]++;
        }
    }

    return statusitems;
});

export const selectPendingServiceDeletions = createSelector(
    selectChServicesState,
    (state: CHServiceState): any[] => state.pendingDeletions
);

export const loadingServiceState = createSelector(selectChServicesState, (state: CHServiceState) => state.callState);

// Reselects the security service using the instance ID because when adding new control points,
// just resending the original service does not pick up new control points
export const selectSecuritySensorList = (securityService: CHService, controlPointMatcher: string) =>
    createSelector(
        CHServiceSelectors.selectChService(securityService?.instanceId),
        DeviceSelectors.getChDevices,
        (securityPartition: CHService, devices: ClareHomeDbCollection<CHDevice>) => {
            if (securityPartition?.controlPoints && devices) {
                // console.log(Object.values(securityPartition.controlPoints));
                const sensorUids = Object.values(securityPartition.controlPoints)
                    .filter(controlPoint => controlPoint.uid.startsWith(controlPointMatcher))
                    .map(controlPoint => controlPoint.deviceUid);
                const childDevices: SecurityZoneInfo[] = [];
                for (const childUid of sensorUids) {
                    const childDevice = DeviceSelectors.getZoneListDevice(childUid, devices);

                    if (childDevice) {
                        childDevices.push(childDevice);
                    }
                }
                return childDevices.sort((a, b) => +a.zoneNumber - +b.zoneNumber);
            }
            return [];
        }
    );

export const selectSecuritySensors = (securityPartition: CHService, controlPointMatcher: string) =>
    createSelector(DeviceSelectors.getChDevices, (devices: ClareHomeDbCollection<CHDevice>) => {
        if (securityPartition?.controlPoints && devices) {
            const fobUids = Object.values(securityPartition.controlPoints)
                .filter(controlPoint => controlPoint.uid.startsWith(controlPointMatcher))
                .map(controlPoint => controlPoint.deviceUid);
            const childDevices: CHDevice[] = [];

            for (const childUid of fobUids) {
                const childDevice = devices[childUid];

                if (childDevice) {
                    childDevices.push(childDevice);
                }
            }
            return childDevices;
        }
        return [];
    });
